import type { LinkProps } from '@mui/material/Link';

import { forwardRef, useLayoutEffect } from 'react';
import { mergeClasses } from 'minimal-shared/utils';

import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';

import { RouterLink } from 'src/routes/components';

import { logoClasses } from './classes';
import { useCheckFeatureFlag } from 'src/lib/hooks/useCheckFeatureFlag';

// ----------------------------------------------------------------------

export type LogoProps = LinkProps & {
  isSingle?: boolean;
  disabled?: boolean;
};

export const Logo = forwardRef<HTMLAnchorElement, LogoProps>((props, ref) => {
  const { className, href = '/', isSingle = true, disabled, sx, ...other } = props;

  const shouldRenderEcollectLogo = useCheckFeatureFlag('bop-should-show-ecollect-logo');

  useLayoutEffect(() => {
    if (shouldRenderEcollectLogo) {
      let favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;

      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = '/favicons/ecollect/favicon.ico';
    }
  }, [shouldRenderEcollectLogo]);

  let singleLogo, fullLogo;

  if (shouldRenderEcollectLogo) {
    singleLogo = (
      <svg
        width="121"
        height="120"
        viewBox="0 0 121 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="0.0620117" width="120" height="120" rx="8" fill="url(#paint0_linear_1212_3491)" />
        <path
          d="M60.485 95.2047C39.452 95.2047 24.062 82.8927 24.062 60.9619V59.9359C24.062 38.3899 40.0933 24.7954 60.3568 24.7954C78.6965 24.7954 94.4713 35.3119 94.4713 59.4229V65.4507H47.2753C47.9165 74.2999 53.0465 79.4299 61.2545 79.4299C68.8213 79.4299 72.0275 75.9672 72.9253 71.4784H94.4713C92.4193 86.6119 80.7485 95.2047 60.485 95.2047ZM47.5318 52.4974H72.1558C71.771 44.4177 67.7953 39.9289 60.3568 39.9289C53.4313 39.9289 48.686 44.4177 47.5318 52.4974Z"
          fill="white"
        />
        <defs>
          <linearGradient
            id="paint0_linear_1212_3491"
            x1="7.94181"
            y1="60"
            x2="120.062"
            y2="60"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#582C83" />
            <stop offset="1" stopColor="#4EC3E0" />
          </linearGradient>
        </defs>
      </svg>
    );
    fullLogo = (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        id="Layer_2"
        data-name="Layer 2"
        viewBox="0 0 401.85 75.25"
      >
        <g id="Layer_1-2" data-name="Layer 1">
          <g>
            <path d="M0,48.98v-.79c0-16.52,12.3-26.95,27.84-26.95,14.07,0,26.16,8.07,26.16,26.56v4.62H17.8c.49,6.79,4.43,10.72,10.72,10.72,5.8,0,8.26-2.66,8.95-6.1h16.52c-1.57,11.61-10.53,18.2-26.07,18.2S0,65.8,0,48.98ZM36.88,42.49c-.29-6.2-3.34-9.64-9.05-9.64-5.31,0-8.95,3.44-9.84,9.64h18.88Z" />
            <path d="M59.5,39.34v-.79c0-21.64,15.34-35.9,35.9-35.9,18.29,0,30.98,8.46,32.66,27.05h-20.75c-.79-7.18-4.72-11.11-11.8-11.11-9.05,0-14.75,7.08-14.75,19.87v.79c0,12.69,5.41,19.87,15.05,19.87,7.08,0,12.1-3.74,13.08-11.51h20.07c-1.38,18.2-14.56,27.64-32.75,27.64-24.59,0-36.69-14.36-36.69-35.9Z" />
            <path d="M133.75,48.98v-.79c0-16.52,12.29-26.95,28.23-26.95s28.03,10.13,28.03,26.46v.79c0,16.72-12.3,26.75-28.13,26.75s-28.13-9.64-28.13-26.26ZM172.11,48.69v-.79c0-8.66-3.44-13.47-10.13-13.47s-10.23,4.62-10.23,13.28v.79c0,8.85,3.44,13.67,10.23,13.67s10.13-4.92,10.13-13.48Z" />
            <path d="M198.17,0h17.51v74.06h-17.51V0Z" />
            <path d="M227.08,0h17.51v74.06h-17.51V0Z" />
            <path d="M252.75,48.98v-.79c0-16.52,12.3-26.95,27.84-26.95,14.07,0,26.16,8.07,26.16,26.56v4.62h-36.2c.49,6.79,4.43,10.72,10.72,10.72,5.8,0,8.26-2.66,8.95-6.1h16.52c-1.57,11.61-10.52,18.2-26.06,18.2s-27.94-9.44-27.94-26.26ZM289.63,42.49c-.29-6.2-3.34-9.64-9.05-9.64-5.31,0-8.95,3.44-9.84,9.64h18.88Z" />
            <path d="M311.56,48.98v-.79c0-17.12,12.3-26.95,27.44-26.95,12.59,0,24.2,5.51,25.18,21.15h-16.52c-.79-4.82-3.15-7.47-8.26-7.47-6.2,0-9.84,4.43-9.84,13.08v.79c0,8.95,3.44,13.57,10.13,13.57,4.92,0,8.36-2.95,8.95-8.56h15.84c-.49,12.98-9.25,21.44-25.87,21.44-15.34,0-27.05-8.95-27.05-26.26Z" />
            <path d="M374.01,57.54v-22.92h-6.2v-12h6.2v-10.72h17.61v10.72h10.13v12h-10.13v21.25c0,3.74,1.87,5.41,5.21,5.41,1.97,0,3.44-.29,5.02-.88v13.38c-2.26.59-5.61,1.48-9.74,1.48-11.61,0-18.1-5.61-18.1-17.71Z" />
          </g>
        </g>
      </svg>
    );
  } else {
    singleLogo = (
      <svg
        width="120"
        height="120"
        viewBox="0 0 120 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_6135_120144)">
          <path
            d="M47.3846 35.955C61.1669 35.9881 72.3664 24.8423 72.3995 11.06C72.3996 11.04 72.3996 11.02 72.3996 11H47.3846V35.955Z"
            fill="black"
          />
          <path
            d="M47.3846 60.9197V109.777H72.3996V35.9694C58.6174 35.9363 47.4178 47.0801 47.3847 60.8597C47.3847 60.8797 47.3846 60.8997 47.3846 60.9197Z"
            fill="black"
          />
        </g>
        <defs>
          <clipPath id="clip0_6135_120144">
            <rect width="25" height="99" fill="white" transform="translate(47.5 11)" />
          </clipPath>
        </defs>
      </svg>
    );

    fullLogo = (
      <svg
        width="232"
        height="100"
        viewBox="0 0 232 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_6135_120135)">
          <path d="M145.9 24.9786H120.885V98.7775H145.9V24.9786Z" fill="black" />
          <path
            d="M83.5932 23.7325C74.1298 23.7325 66.3382 26.5716 60.4393 34.3V24.9786H35.4244V98.7775H60.4236V58.4475C60.4236 49.9304 65.1553 43.7792 74.0982 43.7792C83.0412 43.7792 85.5963 50.2459 85.5963 58.1478V98.7775H110.454V51.35C110.454 43.3061 109.681 37.4703 103.924 31.461C98.3403 25.783 91.3532 23.7325 83.5932 23.7325Z"
            fill="black"
          />
          <path
            d="M0 24.955C13.7822 24.9881 24.9818 13.8423 25.0149 0.06002C25.0149 0.04001 25.015 0.02 25.015 0H0V24.955Z"
            fill="black"
          />
          <path
            d="M0 49.9197V98.7775H25.015V24.9694C11.2327 24.9363 0.03318 36.0801 7e-05 49.8597C2e-05 49.8797 0 49.8997 0 49.9197Z"
            fill="black"
          />
          <path
            d="M153.651 61.7829C153.651 37.5541 172.254 23.7091 192.589 23.7091C212.781 23.7091 231.385 37.5541 231.385 61.7829C231.385 86.0117 212.781 100 192.589 100C172.254 100 153.651 86.0117 153.651 61.7829ZM207.472 61.7895C207.472 52.4347 202.562 44.7165 192.584 44.7165C182.459 44.7165 177.566 52.4347 177.566 61.7895C177.566 71.142 182.576 79.042 192.584 79.042C202.447 79.042 207.472 71.142 207.472 61.7895Z"
            fill="black"
          />
        </g>
        <defs>
          <clipPath id="clip0_6135_120135">
            <rect width="231.385" height="100" fill="white" />
          </clipPath>
        </defs>
      </svg>
    );
  }

  return (
    <LogoRoot
      ref={ref}
      component={RouterLink}
      href={href}
      aria-label="Logo"
      underline="none"
      className={mergeClasses([logoClasses.root, className])}
      sx={[
        () => ({
          ...(!isSingle
            ? {
                width: 224,
                height: 56.9,
                padding: '2.222px 20.273px 3.115px 20.444px',
              }
            : {}),
          ...(disabled && { pointerEvents: 'none' }),
          '& svg': {
            ...(!isSingle
              ? {
                  width: 119.28,
                  height: 51.55,
                }
              : {
                  width: 40,
                  height: 33,
                }),
          },
          justifyContent: isSingle ? 'center' : 'flex-start',
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      {isSingle ? singleLogo : fullLogo}
    </LogoRoot>
  );
});

// ----------------------------------------------------------------------

const LogoRoot = styled(Link)(() => ({
  flexShrink: 0,
  color: 'transparent',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
}));
